<script setup>
import { useNavigationItems } from '@/composables/useNavigationItems'
import { computed, defineAsyncComponent } from 'vue'

const CmsBlock = defineAsyncComponent(
  () => import('@/components/basic/atoms/CmsBlock.vue')
)

const extractPath = (url) => {
  if (!url || url.startsWith('/')) {
    return url
  }

  try {
    const urlObj = new URL(url)
    return urlObj.pathname + urlObj.hash + urlObj.search
  } catch {
    return url
  }
}

const { navigationItems: fetchedItems } = useNavigationItems()

const navigationItems = computed(() =>
  fetchedItems.value.map((item) => ({
    ...item,
    url: extractPath(item.url)
  }))
)
</script>

<template>
  <div
    id="main-navigation"
    class="d-none d-md-flex justify-center font-size-m"
    :style="{
      backgroundColor: `rgb(var(--v-theme-white))`,
      borderTop: `1px solid rgb(var(--v-theme-light-ochre))`,
      borderBottom: `1px solid rgb(var(--v-theme-light-ochre))`
    }"
  >
    <div class="v-container pa-0">
      <div class="d-flex w-100 justify-center">
        <VBtn
          v-for="(navigationItem, index) in navigationItems"
          :key="navigationItem.name"
          :class="navigationItem.css_classes"
          variant="text"
          :color="navigationItem.link_color || 'secondary'"
          min-width="50px"
          flex-shrink="1"
          :to="
            $vuetify.display.platform.touch && navigationItem.flyout
              ? null
              : navigationItem.url
          "
          :append-icon="navigationItem.flyout ? 'custom:down' : ''"
        >
          {{ navigationItem.name }}
          <VMenu
            v-if="navigationItem.flyout"
            activator="parent"
            transition="slide-y-transition"
            bottom
            scroll-strategy="none"
            :open-on-hover="!$vuetify.display.platform.touch"
            target="#main-navigation > .v-container"
          >
            <VCard class="pa-3">
              <VContainer>
                <CmsBlock :blockData="navigationItem.flyout" />
              </VContainer>
            </VCard>
          </VMenu>
        </VBtn>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Shown from md up by the d-none d-md-flex classes alone: TheHeader renders
   this bar at every width, so a server render's viewport guess cannot add or
   remove these 56px after hydration (VIP-12562). */
#main-navigation {
  min-height: 56px;
}

#main-navigation .v-btn {
  flex-shrink: 1;
}

#main-navigation :deep(.v-btn__content) {
  white-space: normal;
}

:deep(.flyout) {
  li.headline {
    margin: 0 10px 10px 10px;
    font-weight: bold;
    text-transform: uppercase;
    border-bottom: 2px solid rgb(var(--v-theme-light-ochre));
    padding: 0.5rem 0;
    letter-spacing: 0.5px;
  }

  a {
    letter-spacing: 0.5px;
  }
}
</style>
